import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, unref as _unref, resolveDynamicComponent as _resolveDynamicComponent, renderList as _renderList } from "vue";
const _hoisted_1 = { class: "form__section-title" };
const _hoisted_2 = {
    key: 0,
    class: "form__bulk-select"
};
const _hoisted_3 = ["checked"];
const _hoisted_4 = {
    key: 2,
    class: "form__validation"
};
import { computed, ref } from "vue";
import FormUtils from "./FormUtils";
import log from "../../services/Logger";
import FormFields from "./FormFields.vue";
import FormLabel from "./FormLabel.vue";
import FieldControlText from "./FieldControlText.vue";
import FieldControlForm from "./FieldControlForm.vue";
import FieldControlTable from "./FieldControlTable.vue";
import FieldControlNumber from "./FieldControlNumber.vue";
import FieldControlBool from "./FieldControlBool.vue";
import FieldControlBoolSelect from "./FieldControlBoolSelect.vue";
import FieldControlTextarea from "./FieldControlTextarea.vue";
import FieldControlHtml from "./FieldControlHtml.vue";
import FieldControlChoiceExpanded from "./FieldControlChoiceExpanded.vue";
import FieldControlChoiceSelection from "./FieldControlChoiceSelection.vue";
import FieldControlEntity from "./FieldControlEntity.vue";
import FieldControlDate from "./FieldControlDate.vue";
import FieldControlDatetime from "./FieldControlDatetime.vue";
import FieldControlFile from "./FieldControlFile.vue";
import FieldControlInfo from "./FieldControlInfo.vue";
import FieldControlRangeTime from "./FieldControlRangeTime.vue";
import FieldControlJson from "./FieldControlJson.vue";
import FieldControlPassword from "./FieldControlPassword.vue";
import FieldControlSecret from "./FieldControlSecret.vue";
import FieldControlCredentials from "./FieldControlCredentials.vue";
import FieldControlLocalized from "./FieldControlLocalized.vue";
import { escapeClass, fieldPath as createFieldPath, groupClasses as createGroupClasses, isSelectionField, resolveFieldControl } from "./fieldControls";
// controls with a single component; boolean and choice depend on the field options
export default /*@__PURE__*/ _defineComponent({
    ...{ inheritAttrs: false },
    __name: 'FormGroup',
    props: {
        form: {},
        field: {},
        data: {},
        validation: {},
        mode: {},
        fieldPrefix: {},
        submitted: { type: Boolean },
        hideLabel: { type: Boolean }
    },
    emits: [
        'change',
        'bulk-select-toggle',
    ],
    setup(__props, { emit: __emit }) {
        const CONTROL_COMPONENTS = {
            'textarea': FieldControlTextarea,
            'html': FieldControlHtml,
            'localized': FieldControlLocalized,
            'json': FieldControlJson,
            'date': FieldControlDate,
            'datetime': FieldControlDatetime,
            'entity': FieldControlEntity,
            'number': FieldControlNumber,
            'form': FieldControlForm,
            'table': FieldControlTable,
            'file': FieldControlFile,
            'range_time': FieldControlRangeTime,
            'input': FieldControlText,
            'info': FieldControlInfo,
            'password': FieldControlPassword,
            'secret': FieldControlSecret,
            'credentials': FieldControlCredentials,
        };
        // the section branch renders the title and its inner fields as sibling root nodes
        const props = __props;
        const emit = __emit;
        // one id per component instance, unique across all forms
        const fieldIdSeq = FormUtils.fieldIdSeq++;
        const touched = ref(props.submitted ?? false);
        const focussed = ref(false);
        const fieldOptions = computed(() => props.field.options || {});
        const fieldControl = computed(() => resolveFieldControl(props.field));
        const fieldId = computed(() => props.form.name + '_' + escapeClass(props.field.name) + '_' + fieldIdSeq);
        const fieldPath = computed(() => createFieldPath(props.fieldPrefix, props.field.name));
        const controlComponent = computed(() => {
            if (fieldOptions.value.component) {
                return fieldOptions.value.component;
            }
            switch (fieldControl.value) {
                case 'boolean':
                    return fieldOptions.value.required ? FieldControlBool : FieldControlBoolSelect;
                case 'choice':
                    return fieldOptions.value.expanded ? FieldControlChoiceExpanded : FieldControlChoiceSelection;
            }
            const component = CONTROL_COMPONENTS[fieldControl.value];
            if (!component) {
                throw 'Unsupported field control: ' + fieldControl.value;
            }
            return component;
        });
        const errors = computed(() => props.validation.fields[fieldPath.value]);
        const showErrors = computed(() => (touched.value || (props.submitted ?? false)) && !!errors.value && errors.value.length > 0);
        const groupClasses = computed(() => createGroupClasses(props.field, fieldControl.value, {
            focussed: focussed.value,
            invalid: showErrors.value,
            mode: props.mode,
        }));
        function onChange(data) {
            log('FormGroup@change', props.field.name, data);
            emit('change', data);
        }
        function onFocus() {
            focussed.value = true;
        }
        function onBlur() {
            touched.value = true;
            focussed.value = false;
        }
        function toggleBulk() {
            emit('bulk-select-toggle', fieldPath.value);
        }
        return (_ctx, _cache) => {
            return (__props.field.type === 'section')
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createElementVNode("div", {
                        class: _normalizeClass(["form__group", groupClasses.value])
                    }, [
                        _createElementVNode("div", _hoisted_1, _toDisplayString(__props.field.label || _ctx.$t(__props.form.name + '_' + __props.field.label)), 1)
                    ], 2),
                    _cache[1] || (_cache[1] = _createTextVNode()),
                    (__props.field.fields && __props.field.fields.length > 0 && __props.mode !== 'bulk')
                        ? (_openBlock(), _createBlock(FormFields, {
                            key: 0,
                            form: __props.form,
                            fields: __props.field.fields,
                            "field-prefix": fieldPath.value,
                            data: __props.data,
                            disabled: fieldOptions.value.disabled ?? undefined,
                            validation: __props.validation,
                            submitted: __props.submitted,
                            onChange: onChange
                        }, null, 8, ["form", "fields", "field-prefix", "data", "disabled", "validation", "submitted"]))
                        : _createCommentVNode("", true)
                ], 64))
                : (_openBlock(), _createElementBlock("div", {
                    key: 1,
                    class: _normalizeClass(["form__group", groupClasses.value])
                }, [
                    (__props.mode === 'bulk')
                        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                            _createElementVNode("input", {
                                type: "checkbox",
                                name: "bulk-selected",
                                checked: !fieldOptions.value.disabled,
                                onClick: _cache[0] || (_cache[0] = ($event) => (toggleBulk()))
                            }, null, 8, _hoisted_3)
                        ]))
                        : _createCommentVNode("", true),
                    _cache[4] || (_cache[4] = _createTextVNode()),
                    (!__props.hideLabel)
                        ? (_openBlock(), _createBlock(FormLabel, {
                            key: 1,
                            form: __props.form,
                            field: __props.field,
                            "field-id": fieldId.value
                        }, null, 8, ["form", "field", "field-id"]))
                        : _createCommentVNode("", true),
                    _cache[5] || (_cache[5] = _createTextVNode()),
                    _createElementVNode("div", {
                        class: _normalizeClass(["form__control-wrapper", {
                                'form__control-wrapper--nested': fieldControl.value === 'form' || fieldControl.value === 'table',
                                'form__control-wrapper--table': fieldControl.value === 'table',
                                'form__selection': _unref(isSelectionField)(__props.field, fieldControl.value)
                            }])
                    }, [
                        (fieldOptions.value.componentBefore)
                            ? (_openBlock(), _createBlock(_resolveDynamicComponent(fieldOptions.value.componentBefore), {
                                key: 0,
                                data: __props.data,
                                field: __props.field,
                                form: __props.form,
                                validation: __props.validation,
                                params: fieldOptions.value.params,
                                submitted: __props.submitted ?? false
                            }, null, 8, ["data", "field", "form", "validation", "params", "submitted"]))
                            : _createCommentVNode("", true),
                        _cache[2] || (_cache[2] = _createTextVNode()),
                        (_openBlock(), _createBlock(_resolveDynamicComponent(controlComponent.value), {
                            form: __props.form,
                            field: __props.field,
                            "field-prefix": __props.fieldPrefix ?? '',
                            "field-id": fieldId.value,
                            data: __props.data,
                            validation: __props.validation,
                            submitted: __props.submitted ?? false,
                            onChange: onChange,
                            onFocus: onFocus,
                            onBlur: onBlur
                        }, null, 40, ["form", "field", "field-prefix", "field-id", "data", "validation", "submitted"])),
                        _cache[3] || (_cache[3] = _createTextVNode()),
                        (fieldOptions.value.componentAfter)
                            ? (_openBlock(), _createBlock(_resolveDynamicComponent(fieldOptions.value.componentAfter), {
                                key: 1,
                                data: __props.data,
                                field: __props.field,
                                form: __props.form,
                                validation: __props.validation,
                                params: fieldOptions.value.params,
                                submitted: __props.submitted ?? false
                            }, null, 8, ["data", "field", "form", "validation", "params", "submitted"]))
                            : _createCommentVNode("", true)
                    ], 2),
                    _cache[6] || (_cache[6] = _createTextVNode()),
                    (showErrors.value)
                        ? (_openBlock(), _createElementBlock("ul", _hoisted_4, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(errors.value, (error, index) => {
                                return (_openBlock(), _createElementBlock("li", {
                                    key: index,
                                    class: "form__validation-item"
                                }, _toDisplayString((typeof error === "string") ? _ctx.$t(error) : error.message), 1));
                            }), 128))
                        ]))
                        : _createCommentVNode("", true)
                ], 2));
        };
    }
});
